import { useState } from 'react';
import type { App } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hasEasProject } from '@/features/apps/easSetupModel';
import { RepoFilesSheet } from './RepoFilesSheet';
import { repoFileNames } from './repoFilesModel';

/** "View files" and the sheet it opens; `testId` names the button. */
export function RepoFilesButton({ app, testId = 'repo-files-open', variant = 'outline' }: { app: App; testId?: string; variant?: 'outline' | 'default' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button type="button" variant={variant} className="w-fit" data-testid={testId} onClick={() => setOpen(true)}>
        <Icon name="description" size={18} />
        View files
      </Button>
      <RepoFilesSheet app={app} open={open} onOpenChange={setOpen} />
    </>
  );
}

/** App page (Expo / React Native apps): what the app's folder needs before its first build, filled for this app (SPEC decision 29). */
export function RepoFilesCard({ app }: { app: App }) {
  if (!hasEasProject(app.framework)) return null;
  return (
    <Card className="enter" style={stagger(2)} data-testid="app-repo-files">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon name="folder_open" size={18} className="text-subtle" />
          Repository files
        </CardTitle>
        <CardDescription>What this app’s folder needs before its first build, filled for this app. Hangar never writes to your repository.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="flex flex-wrap gap-1.5" aria-label="Files">
          {repoFileNames(app.framework).map((name) => (
            <li key={name}>
              <Tag className="text-foreground">{name}</Tag>
            </li>
          ))}
        </ul>
        <RepoFilesButton app={app} />
      </CardContent>
    </Card>
  );
}
